import { skipToken } from '@reduxjs/toolkit/query';
import clsx from 'clsx';
import { DateTime } from 'luxon';
import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { AvatarDisplay } from '@/app/components/avatar-display.tsx';
import { OnboardImportStatusIndicator } from '@/app/components/onboarding/onboard-import-status-indicator.tsx';
import { usePageLoading } from '@/app/components/page-loading.tsx';
import { formatDate } from '@/app/helpers/date.helpers.ts';
import { useFetchOnboardImportQuery } from '@/slices/onboard-import-api-slice.ts';
import {
  type OnboardImportLineItemBaseModel,
  OnboardImportStatusTypeEnum,
} from '../../types/onboard-import.type.ts';
import OnboardImportTabs from './onboard-import-tabs.tsx';

const LineItemStatus = ({
  lineItems,
  title,
  isComplete,
}: {
  lineItems: OnboardImportLineItemBaseModel[];
  title: string;
  isComplete: boolean;
}) => {
  const importedCount =
    lineItems.filter(val =>
      val.onboardImportStatuses.some(
        x => x.onboardImportStatusType.id === OnboardImportStatusTypeEnum.Success,
      ),
    )?.length ?? 0;
  const importedPercent = Math.round((importedCount / (lineItems.length || 1)) * 100);
  const errors =
    lineItems.filter(val =>
      val.onboardImportStatuses.some(
        x => x.onboardImportStatusType.id === OnboardImportStatusTypeEnum.Error,
      ),
    )?.length ?? 0;
  const warnings =
    lineItems.filter(val =>
      val.onboardImportStatuses.some(
        x => x.onboardImportStatusType.id === OnboardImportStatusTypeEnum.Warning,
      ),
    )?.length ?? 0;
  const success =
    lineItems.filter(val =>
      val.onboardImportStatuses.some(
        x => x.onboardImportStatusType.id === OnboardImportStatusTypeEnum.Success,
      ),
    )?.length ?? 0;
  return (
    <div className='px-4 py-5 sm:p-6'>
      <dt className='text-base font-normal text-gray-900'>{title}</dt>
      <dd className='mt-1 flex items-baseline justify-between md:block lg:flex'>
        <div className='flex items-baseline text-2xl font-semibold text-secondary-600'>
          {importedCount}
          <span className='ml-2 text-sm font-medium text-gray-500'>of {lineItems.length}</span>
          <span
            className={clsx(
              'ml-2 font-medium',
              !isComplete
                ? 'text-primary-500'
                : importedPercent < 100
                  ? 'text-red-500'
                  : 'text-green-500',
            )}
          >
            {importedPercent}%
          </span>
        </div>
        <div className='px-4 py-4 sm:px-6'>
          <div className='text-sm'>
            <span className='ml-2 text-sm font-medium text-green-500'>{success} Completed</span>
            <span className='ml-2 text-sm font-medium text-amber-500'>{warnings} Warnings</span>
            <span className='ml-2 text-sm font-medium text-red-500'>{errors} Errors</span>
          </div>
        </div>
      </dd>
    </div>
  );
};

const OnboardImportView = () => {
  const params = useParams();
  const { data, isLoading } = useFetchOnboardImportQuery(params.key ?? skipToken);
  const { showPageLoading, hidePageLoading } = usePageLoading();

  useEffect(() => {
    isLoading
      ? showPageLoading('loading-onboard-import')
      : hidePageLoading('loading-onboard-import');
  }, [isLoading]);

  const getTextClass = (statusTypeId: OnboardImportStatusTypeEnum) => {
    switch (statusTypeId) {
      case OnboardImportStatusTypeEnum.Success:
        return 'text-green-600';
      case OnboardImportStatusTypeEnum.Error:
        return 'text-red-600';
      case OnboardImportStatusTypeEnum.Warning:
        return 'text-yellow-600';
      case OnboardImportStatusTypeEnum.Information:
        return 'text-gray-600';
      default:
        return 'text-secondary-600';
    }
  };

  return (
    <>
      <div className='border-b border-gray-200 pb-5'>
        <h3 className='text-base font-semibold text-gray-900'>
          {data?.account.accountName} Import Status
        </h3>
      </div>
      <dl className='mt-5 grid grid-cols-1 divide-y divide-gray-200 overflow-hidden rounded-lg bg-white shadow md:grid-cols-4 md:divide-x md:divide-y-0'>
        <div className='px-4 py-5 sm:p-6'>
          <dt className='text-base font-normal text-gray-900'>Status</dt>
          <dd className='mt-1 flex items-baseline justify-between md:block lg:flex'>
            <div
              className={clsx(
                'flex items-center text-2xl font-semibold',
                getTextClass(data?.onboardImportStatusType.id),
              )}
            >
              <OnboardImportStatusIndicator status={data?.onboardImportStatusType} />
              &nbsp;
              {data?.onboardImportStatusType?.description ?? 'In progress'}
              <span className='ml-2 font-medium text-gray-500'>{data?.statusNotes}</span>
            </div>
          </dd>
        </div>

        <div className='px-4 py-5 sm:p-6'>
          <dt className='text-base font-normal text-gray-900'>Uploaded</dt>
          <dd className='mt-1 flex items-baseline justify-between md:block lg:flex'>
            <div className='flex items-baseline text-2xl font-semibold text-secondary-600'>
              {formatDate(data?.uploadedAt, DateTime.DATETIME_SHORT_WITH_SECONDS, '')}
            </div>
          </dd>
        </div>

        <div className='px-4 py-5 sm:p-6'>
          <dt className='text-base font-normal text-gray-900'>Start - End</dt>
          <dd className='mt-1 flex items-baseline justify-between md:block lg:flex'>
            <div className='flex items-baseline text-2xl font-semibold text-secondary-600'>
              {DateTime.fromISO(data?.startedAt).isValid
                ? `${formatDate(data?.startedAt, DateTime.DATETIME_SHORT_WITH_SECONDS)} - `
                : 'Not started'}
              <span className='ml-2 font-medium text-secondary-600'>
                {formatDate(data?.completedAt, DateTime.DATETIME_SHORT_WITH_SECONDS, '')}
              </span>
            </div>
          </dd>
        </div>
        <div className='px-4 py-5 sm:p-6'>
          <dt className='text-base font-normal text-gray-900'>Uploaded by</dt>
          <dd className='mt-1 flex items-baseline justify-between md:block lg:flex'>
            <div className='flex items-center text-2xl font-semibold text-secondary-600'>
              <AvatarDisplay
                className='size-10'
                firstName={data?.importedByUser.firstName}
                imageUrl={data?.importedByUser.profilePhotoUrl}
                lastName={data?.importedByUser.lastName}
              />
              <span className='ml-2 font-medium text-gray-500'>
                {data?.importedByUser.firstName} {data?.importedByUser.lastName}
              </span>
            </div>
          </dd>
        </div>
      </dl>
      <dl className='mt-5 grid grid-cols-1 divide-y divide-gray-200 overflow-hidden rounded-lg bg-white shadow md:grid-cols-4 md:divide-x md:divide-y-0'>
        <LineItemStatus
          isComplete={
            data?.onboardStaffImportLineItems.some(x => x.onboardImportStatuses?.length) ||
            data?.percentComplete === 100
          }
          lineItems={data?.onboardRepresentativeImportLineItems ?? []}
          title='Reps import status'
        />
        <LineItemStatus
          isComplete={
            data?.onboardMeetingImportLineItems.some(x => x.onboardImportStatuses?.length) ||
            data?.percentComplete === 100
          }
          lineItems={data?.onboardStaffImportLineItems ?? []}
          title='Staff import status'
        />
        <LineItemStatus
          isComplete={
            data?.onboardAvailabilitySlotImportLineItems.some(
              x => x.onboardImportStatuses.length,
            ) || data?.percentComplete === 100
          }
          lineItems={data?.onboardMeetingImportLineItems ?? []}
          title='Meeting import status'
        />
        <LineItemStatus
          isComplete={data?.percentComplete === 100}
          lineItems={data?.onboardAvailabilitySlotImportLineItems ?? []}
          title='Availability slot import status'
        />
      </dl>
      <div className='py-5'>
        <h4 className='sr-only'>Status</h4>
        <p className='font-medium text-gray-900'>
          {data?.percentComplete === 100 ? 'Import Complete' : 'Importing Data...'}{' '}
          {Math.round(data?.percentComplete ?? 0)}%
        </p>
        <div aria-hidden='true' className='mt-6'>
          <div className='overflow-hidden rounded-full bg-gray-200'>
            <div
              style={{ width: `${data?.percentComplete ?? 0}%` }}
              className={clsx(
                'h-3 rounded-full',
                data?.percentComplete === 100 ? 'bg-green-600' : 'bg-secondary-600',
              )}
            />
          </div>
        </div>
      </div>
      <OnboardImportTabs onboardImport={data} />
    </>
  );
};

export default OnboardImportView;
